import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Clock, Mail, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CampaignTab({ formatDate }) {
  const [campaignEmails, setCampaignEmails] = useState("");
  const [campaignSubject, setCampaignSubject] = useState("-500€ sur votre enseigne lumineuse (offre limitée)");
  const [isSending, setIsSending] = useState(false);
  const [campaignResult, setCampaignResult] = useState(null);
  const [campaignHistory, setCampaignHistory] = useState([]);

  const fetchCampaignHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/campaign/history`);
      setCampaignHistory(res.data);
    } catch {}
  };

  useState(() => { fetchCampaignHistory(); }, []);

  const sendCampaign = async () => {
    const emails = campaignEmails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e && e.includes("@"));
    if (emails.length === 0) { toast.error("Ajoutez au moins un email valide"); return; }
    setIsSending(true);
    setCampaignResult(null);
    try {
      const res = await axios.post(`${API}/admin/campaign/send`, { emails, subject: campaignSubject });
      setCampaignResult(res.data);
      toast.success(`${res.data.sent} emails envoyés sur ${res.data.total}`);
      fetchCampaignHistory();
    } catch {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Send className="w-5 h-5 text-[#EFFF00]" />
            Envoyer une campagne
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#A3A3A3] mb-2">Objet de l'email</label>
              <Input
                value={campaignSubject}
                onChange={(e) => setCampaignSubject(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white"
                data-testid="campaign-subject"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#A3A3A3] mb-2">
                Emails des destinataires
                <span className="text-[#666] ml-2">(1 par ligne ou séparés par des virgules)</span>
              </label>
              <textarea
                value={campaignEmails}
                onChange={(e) => setCampaignEmails(e.target.value)}
                rows={8}
                placeholder={"commercant1@gmail.com\ncommercant2@gmail.com\nboutique@email.fr"}
                className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#EFFF00]/50 placeholder-[#444] resize-none"
                data-testid="campaign-emails"
              />
              <p className="text-[#666] text-xs mt-1">
                {campaignEmails.split(/[\n,;]+/).filter(e => e.trim() && e.includes("@")).length} email(s) détecté(s)
              </p>
            </div>

            <Button
              onClick={sendCampaign}
              disabled={isSending}
              className="w-full bg-[#EFFF00] text-black hover:bg-[#d4e300] font-bold py-3 rounded-xl disabled:opacity-50"
              data-testid="campaign-send-btn"
            >
              {isSending ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Envoi en cours...
                </span>
              ) : (
                <span className="flex items-center gap-2"><Send className="w-4 h-4" /> Envoyer la campagne</span>
              )}
            </Button>

            {campaignResult && (
              <div className={`p-4 rounded-xl border ${campaignResult.failed === 0 ? "bg-[#25D366]/10 border-[#25D366]/30" : "bg-[#EFFF00]/10 border-[#EFFF00]/30"}`} data-testid="campaign-result">
                <p className="font-bold text-white">Résultat de l'envoi :</p>
                <p className="text-[#25D366] text-sm mt-1">{campaignResult.sent} envoyé(s) sur {campaignResult.total}</p>
                {campaignResult.failed > 0 && <p className="text-red-400 text-sm mt-1">{campaignResult.failed} échec(s)</p>}
                {campaignResult.errors.length > 0 && (
                  <div className="mt-2 text-xs text-[#666]">
                    {campaignResult.errors.map((err, i) => <p key={i}>{err}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#EFFF00]" />
              Historique des campagnes
            </h2>
            {campaignHistory.length === 0 ? (
              <p className="text-[#666] text-sm">Aucune campagne envoyée</p>
            ) : (
              <div className="space-y-3">
                {campaignHistory.map((c, i) => (
                  <div key={i} className="bg-[#0A0A0A] rounded-xl p-4 border border-[#1a1a1a]">
                    <p className="text-white text-sm font-medium truncate">{c.subject}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span className="text-[#25D366]">{c.sent} envoyé(s)</span>
                      {c.failed > 0 && <span className="text-red-400">{c.failed} échec(s)</span>}
                      <span className="text-[#666]">{formatDate(c.sent_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#EFFF00]" />
              Aperçu de l'email
            </h2>
            <p className="text-[#A3A3A3] text-sm mb-4">L'email envoyé contient vos réalisations, les tarifs, l'offre -500€ et les boutons CTA vers simulation-enseigne.fr</p>
            <a href="/emailing.html" target="_blank" className="inline-flex items-center gap-2 text-[#EFFF00] text-sm font-medium hover:underline" data-testid="campaign-preview-link">
              <FileText className="w-4 h-4" /> Voir l'aperçu de l'email
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
